
import React, { useState, useEffect } from 'react';

interface QRModalProps {
  onClose: () => void;
  inline?: boolean;
}

export const QRModal: React.FC<QRModalProps> = ({ onClose, inline = false }) => {
  const [qrUrl, setQrUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [rawUrl, setRawUrl] = useState('');

  useEffect(() => {
    let detectedUrl = window.location.href;
    try {
      const urlObj = new URL(detectedUrl);
      urlObj.search = ""; 
      urlObj.hash = "";
      detectedUrl = urlObj.toString();
    } catch (e) {
      detectedUrl = 'https://kinetic-harmony.vercel.app';
    }
    
    setRawUrl(detectedUrl);
    const generatedQr = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(detectedUrl)}&margin=10&format=png`;
    setQrUrl(generatedQr);
  }, []);

  // Listen for Escape key to close modal
  useEffect(() => {
    if (inline) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inline, onClose]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(rawUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback if clipboard API unavailable
    }
  };

  const content = (
    <div 
      className={`w-full ${inline ? '' : 'max-w-md'} bg-slate-900 border border-white/10 rounded-[2.5rem] p-6 sm:p-8 text-center relative shadow-2xl overflow-hidden pointer-events-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-[-20%] left-[-20%] w-[60%] h-[60%] bg-indigo-500/10 rounded-full blur-[80px]"></div>
        <div className="absolute bottom-[-20%] right-[-20%] w-[60%] h-[60%] bg-purple-500/10 rounded-full blur-[80px]"></div>
      </div>

      {!inline && (
        <button 
          id="close-qr-modal-top-btn"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close QR Modal"
          className="absolute top-5 right-5 z-30 p-2.5 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white border border-white/10 transition cursor-pointer active:scale-95 shadow-md flex items-center justify-center min-w-[36px] min-h-[36px]"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <div className="relative z-10 space-y-5">
        <header className="space-y-1.5 pr-6 sm:pr-0">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-[11px] font-bold text-indigo-300 uppercase tracking-widest mb-1">
            <span>📱 Mobile Gyro & Touch</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight italic">Sync with Mobile</h2>
          <p className="text-slate-400 text-xs leading-relaxed px-2 sm:px-4">
            Scan with your phone's camera to turn your smartphone into a wireless somatic motion instrument.
          </p>
        </header>

        <div className="bg-white p-3.5 sm:p-4 rounded-[1.5rem] shadow-xl inline-block min-w-[180px] min-h-[180px] flex items-center justify-center mx-auto border-4 border-indigo-500/20">
          {qrUrl ? (
            <img 
              src={qrUrl} 
              alt="Scan for Mobile Experience" 
              className="w-44 h-44 sm:w-48 sm:h-48 mx-auto"
            />
          ) : (
            <div className="w-44 h-44 sm:w-48 sm:h-48 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <button
            id="copy-mobile-link-btn"
            type="button"
            onClick={handleCopyLink}
            className="w-full py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-white/10 text-xs font-semibold text-slate-300 hover:text-white transition flex items-center justify-center gap-2 cursor-pointer active:scale-95"
          >
            {copied ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-400">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                </svg>
                <span className="text-emerald-400 font-bold">Link Copied to Clipboard!</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="w-4 h-4 text-indigo-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                </svg>
                <span>Copy App URL</span>
              </>
            )}
          </button>

          {!inline && (
            <button
              id="close-qr-modal-bottom-btn"
              type="button"
              onClick={onClose}
              className="w-full py-3 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold uppercase tracking-wider transition shadow-lg shadow-indigo-600/30 cursor-pointer active:scale-95 flex items-center justify-center"
            >
              Done / Close
            </button>
          )}
        </div>

        <footer className="pt-1">
           <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">
              iOS Safari & Android Chrome • Zero App Install
           </p>
        </footer>
      </div>
    </div>
  );

  if (inline) return content;

  return (
    <div 
      id="qr-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-modal-heading"
      onClick={onClose}
      className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/90 backdrop-blur-xl p-4 animate-fade-in cursor-pointer"
    >
      {content}
    </div>
  );
};

